import { humanizeError } from "@/lib/errors";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, MoreHorizontal, Pencil, Plus, RefreshCw, Scale, ShieldAlert, Trash2 } from "lucide-react";

import {
  APIError,
  deletePolicyRule,
  getPolicy,
  listAccessTokens,
  listAudit,
  setCapturePayloads,
  setPolicyDefault,
  setPolicyMode,
  type AccessToken,
  type AuditEntry,
  type PolicyAction,
  type PolicyDefaultAction,
  type PolicyMode,
  type PolicyRule,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/ui/toast";
import { useConfirm } from "@/components/ui/confirm";
import { CopyButton, Spinner } from "@/components/Page";
import { cn } from "@/lib/utils";

function errMsg(e: unknown, fallback: string) {
  return humanizeError(e, fallback);
}
const is403 = (e: unknown) => e instanceof APIError && e.status === 403;

const ACTION_CLS: Record<PolicyAction, string> = {
  ALLOW: "border-success/30 bg-success/10 text-success",
  HOLD: "border-warning/40 bg-warning/10 text-warning",
  DENY: "border-destructive/40 bg-destructive/10 text-destructive",
};

function ActionBadge({ action }: { action: PolicyAction }) {
  return (
    <Badge variant="outline" className={cn("font-mono text-[11px]", ACTION_CLS[action])}>
      {action}
    </Badge>
  );
}

// prettyJSON formats a captured request body for display. The preview may be
// truncated mid-JSON, so fall back to the raw string when it doesn't parse.
function prettyJSON(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function PolicyTab({ projectId }: { projectId: string }) {
  const [mode, setMode] = useState<PolicyMode>("audit");
  const [defaultAction, setDefaultAction] = useState<PolicyDefaultAction>("");
  const [savingDefault, setSavingDefault] = useState(false);
  const [capture, setCapture] = useState(false);
  const [rules, setRules] = useState<PolicyRule[]>([]);
  const [tokens, setTokens] = useState<AccessToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [savingMode, setSavingMode] = useState(false);
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const [pol, toks] = await Promise.all([
        getPolicy(projectId),
        listAccessTokens(projectId).catch(() => [] as AccessToken[]),
      ]);
      setMode(pol.mode);
      setDefaultAction(pol.default_action ?? "");
      setCapture(pol.capture);
      setRules(pol.rules ?? []);
      setTokens(toks);
      setErr(null);
    } catch (e) {
      setErr(errMsg(e, "Couldn't load policy"));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const principalName = useCallback(
    (id?: string) => {
      if (!id) return "Any caller";
      return tokens.find((t) => t.id === id)?.name ?? id;
    },
    [tokens],
  );

  const onSetMode = useCallback(
    async (next: PolicyMode) => {
      if (next === mode) return;
      if (next === "enforce") {
        const ok = await confirm({
          title: "Switch to enforce?",
          description:
            "In enforce mode, DENY/HOLD verdicts BLOCK calls (unknown callers are always blocked). " +
            (defaultAction === "DENY"
              ? "Your default is deny-all, so EVERY tool without an explicit allow rule will be blocked — make sure your allow rules cover what this agent needs."
              : defaultAction === "HOLD"
                ? "Your default is hold-all, so EVERY tool without an explicit allow rule will wait for approval — make sure a channel is configured and allow routine tools."
                : "Your default is allow-all, so only your explicit DENY/HOLD rules block.") +
            " Watch the audit log in audit mode first.",
          confirmText: "Enforce",
          destructive: true,
        });
        if (!ok) return;
      }
      setSavingMode(true);
      try {
        await setPolicyMode(projectId, next);
        setMode(next);
        toast.success(next === "enforce" ? "Policy is now enforced." : "Policy set to audit (log-only).");
      } catch (e) {
        toast.error(is403(e) ? "You need admin access to change the mode." : errMsg(e, "Couldn't change mode"));
      } finally {
        setSavingMode(false);
      }
    },
    [mode, defaultAction, projectId, confirm, toast],
  );

  const onSetDefault = useCallback(
    async (next: PolicyDefaultAction) => {
      const cur = defaultAction === "" ? "ALLOW" : defaultAction;
      if (next === cur) return;
      // Deny-all / hold-all are the consequential ones: while enforcing they
      // immediately gate every tool without an explicit allow rule.
      if (next === "DENY") {
        const ok = await confirm({
          title: "Deny everything by default?",
          description:
            mode === "enforce"
              ? "Every tool call WITHOUT an explicit allow rule will be blocked immediately. Add allow rules for the tools this agent needs first, or you'll lock it out."
              : "Calls without an allow rule will be marked denied (logged only until you switch to enforce). Add allow rules for what this agent needs, then enforce.",
          confirmText: "Deny by default",
          destructive: true,
        });
        if (!ok) return;
      } else if (next === "HOLD") {
        const ok = await confirm({
          title: "Hold everything by default?",
          description:
            "Every call without an explicit allow rule will require human approval" +
            (mode === "enforce" ? " — immediately." : " (logged only until you switch to enforce).") +
            " Holds appear in the Channels tab; configure a channel so they get seen, and add ALLOW rules for routine tools to avoid approval fatigue.",
          confirmText: "Hold by default",
          destructive: true,
        });
        if (!ok) return;
      }
      setSavingDefault(true);
      try {
        await setPolicyDefault(projectId, next);
        setDefaultAction(next);
        toast.success("Default updated.");
      } catch (e) {
        toast.error(is403(e) ? "You need admin access to change this." : errMsg(e, "Couldn't change default"));
      } finally {
        setSavingDefault(false);
      }
    },
    [defaultAction, mode, projectId, confirm, toast],
  );

  const onToggleCapture = useCallback(
    async (next: boolean) => {
      setCapture(next); // optimistic
      try {
        await setCapturePayloads(projectId, next);
        toast.success(next ? "Capturing request payloads." : "Stopped capturing payloads.");
      } catch (e) {
        setCapture(!next); // revert
        toast.error(is403(e) ? "You need admin access to change this." : errMsg(e, "Couldn't change capture"));
      }
    },
    [projectId, toast],
  );

  const onDelete = useCallback(
    async (rule: PolicyRule) => {
      const ok = await confirm({
        title: "Delete rule?",
        description: `This removes the ${rule.action} rule${rule.match.tool ? ` for ${rule.match.tool}` : ""}.`,
        confirmText: "Delete",
        destructive: true,
      });
      if (!ok) return;
      try {
        await deletePolicyRule(projectId, rule.id);
        setRules((cur) => cur.filter((r) => r.id !== rule.id));
        toast.success("Rule deleted.");
      } catch (e) {
        toast.error(is403(e) ? "You need admin access to edit policy." : errMsg(e, "Delete failed"));
      }
    },
    [projectId, confirm, toast],
  );

  return (
    <div className="space-y-6">
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Authorization policy</CardTitle>
          <CardDescription>
            By default every call is allowed, like a normal MCP endpoint. Add rules to deny or hold specific tools —
            or flip the default below to deny-all / hold-all and allow only what you list.
          </CardDescription>
        </div>
        <Button size="sm" onClick={() => navigate(`/agents/${projectId}/policy/rules/new`)}>
          <Plus className="h-4 w-4" /> New rule
        </Button>
      </CardHeader>
      <CardContent className="space-y-5">
        <ModeControl mode={mode} saving={savingMode} onSet={onSetMode} />
        <DefaultActionControl value={defaultAction} saving={savingDefault} onSet={onSetDefault} />

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Spinner /> Loading…
          </div>
        ) : err ? (
          <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
            <span className="text-destructive">{err}</span>
            <Button variant="outline" size="sm" onClick={() => void refresh()}>
              Retry
            </Button>
          </div>
        ) : rules.length === 0 ? (
          <button
            onClick={() => navigate(`/agents/${projectId}/policy/rules/new`)}
            className="flex w-full flex-col items-center gap-2 rounded-md border border-dashed py-10 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/30"
          >
            <Scale className="h-6 w-6 opacity-50" />
            <span>
              No custom rules. <span className="font-medium text-foreground">Add a rule</span> to allow, hold, or deny
              specific tools.{" "}
              {defaultAction === "DENY"
                ? "Every call is denied until you allow one."
                : defaultAction === "HOLD"
                  ? "Every call needs approval until you allow one."
                  : "Every call is allowed until you restrict one."}
            </span>
          </button>
        ) : (
          <ul className="divide-y rounded-md border">
            {rules.map((rule) => (
              <li key={rule.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  <ActionBadge action={rule.action} />
                  <div className="min-w-0">
                    <div className="truncate text-sm">
                      <span className="font-mono">{rule.match.tool || "any tool"}</span>
                      <span className="text-muted-foreground"> · {principalName(rule.match.principal)}</span>
                      {rule.match.args && rule.match.args.length > 0 && (
                        <span className="text-muted-foreground">
                          {" "}
                          · {rule.match.args.length} condition{rule.match.args.length === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {!rule.enabled && <span className="text-warning">disabled · </span>}
                      {rule.reason || <span className="italic">no reason given</span>}
                    </div>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label="Rule actions">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => navigate(`/agents/${projectId}/policy/rules/${rule.id}/edit`)}>
                      <Pencil /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => void onDelete(rule)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
    <AuditActivityCard
      projectId={projectId}
      principalName={principalName}
      capture={capture}
      onToggleCapture={onToggleCapture}
    />
    </div>
  );
}

// --- audit log viewer (GCP-Cloud-Logging-style: dense rows in a fixed-height
// box, click a row to expand its full detail inline) ---

// Sev is a row's severity bucket — drives the left rail color AND the filter.
// It mirrors StatusPill's branch order so the rail can never disagree with the
// pill text.
type Sev = "error" | "warn" | "ok" | "muted";
function auditSeverity(e: AuditEntry): Sev {
  if (e.status === "rejected" || e.status === "denied" || e.status === "error" || e.status === "unknown_principal")
    return "error";
  if (e.status === "ok" && (e.verdict === "DENY" || e.verdict === "HOLD")) return "warn"; // "ran (audit)" would-block
  if (e.status === "approved") return "ok";
  return "muted";
}
const SEV_BAR: Record<Sev, string> = {
  error: "bg-destructive",
  warn: "bg-warning",
  ok: "bg-success",
  muted: "bg-border",
};

// fmtTs is the compact 24h local timestamp for the row gutter
// ("Jun 13 14:03:21"); the full date+year lives in the expanded "When" row.
function fmtTs(at: string): string {
  const d = new Date(at);
  return (
    d.toLocaleDateString(undefined, { month: "short", day: "2-digit" }) +
    " " +
    d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })
  );
}

// One grid template shared by the sticky header AND every row so the columns
// can never drift: caret · time · status · verdict · tool · caller · detail.
// min-w keeps the flexible columns readable; the box scrolls horizontally
// below that width instead of collapsing them.
const ROW_COLS =
  "grid min-w-[680px] grid-cols-[14px_128px_104px_52px_minmax(0,1fr)_140px_minmax(0,1.1fr)] items-center gap-2";

type SevFilter = "all" | "error" | "warn" | "ok";
const SEV_FILTERS: { value: SevFilter; label: string; active: string }[] = [
  { value: "all", label: "All", active: "bg-primary/10 text-primary" },
  { value: "error", label: "Errors", active: "bg-destructive/15 text-destructive" },
  { value: "warn", label: "Would-block", active: "bg-warning/20 text-warning" },
  { value: "ok", label: "Approvals", active: "bg-success/15 text-success" },
];
function matchesFilter(e: AuditEntry, f: SevFilter): boolean {
  return f === "all" || auditSeverity(e) === f;
}

// AuditActivityCard shows recent /mcp tool calls + their policy verdicts so
// an operator can watch what `enforce` would do while in audit mode.
function AuditActivityCard({
  projectId,
  principalName,
  capture,
  onToggleCapture,
}: {
  projectId: string;
  principalName: (id?: string) => string;
  capture: boolean;
  onToggleCapture: (next: boolean) => void;
}) {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [canViewPayloads, setCanViewPayloads] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [filter, setFilter] = useState<SevFilter>("all");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listAudit(projectId, 100);
      setEntries(r.entries);
      setCanViewPayloads(r.canViewPayloads);
      setOpenIdx(null); // a fresh load collapses any open row
      setErr(null);
    } catch (e) {
      setErr(errMsg(e, "Couldn't load activity"));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Keep each entry's original index so openIdx + keys stay stable across filter
  // changes (a hidden open row simply isn't rendered).
  const shown = entries.map((e, i) => ({ e, i })).filter(({ e }) => matchesFilter(e, filter));

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">Recent activity</CardTitle>
          <CardDescription>
            The latest tool calls and the policy verdict each got. In audit mode a{" "}
            <span className="font-mono">DENY</span> or <span className="font-mono">HOLD</span> verdict on a call that
            still ran is what <span className="font-medium">enforce</span> would block or hold.
          </CardDescription>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <label
            className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground"
            title="Persist each call's arguments so you can inspect them here. Payloads may contain secrets/PII and are visible to admins only."
          >
            <input
              type="checkbox"
              checked={capture}
              onChange={(e) => onToggleCapture(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-input"
            />
            Capture payloads
          </label>
          <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && entries.length === 0 ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Spinner /> Loading…
          </div>
        ) : err ? (
          <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
            <span className="text-destructive">{err}</span>
            <Button variant="outline" size="sm" onClick={() => void refresh()}>
              Retry
            </Button>
          </div>
        ) : entries.length === 0 ? (
          <div className="rounded-md border border-dashed py-10 text-center text-sm text-muted-foreground">
            No activity yet. Call a tool through this agent's <span className="font-mono">/mcp</span> endpoint and refresh.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <div className="inline-flex shrink-0 overflow-hidden rounded-md border">
                {SEV_FILTERS.map((f) => (
                  <button
                    key={f.value}
                    onClick={() => setFilter(f.value)}
                    className={cn(
                      "px-2.5 py-1 text-xs font-medium transition-colors",
                      filter === f.value ? f.active : "bg-background text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">showing latest {entries.length}</span>
            </div>
            <div className="h-[28rem] overflow-auto rounded-md border">
              <div
                className={cn(
                  ROW_COLS,
                  "sticky top-0 z-10 border-b bg-card px-2 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground",
                )}
              >
                <span />
                <span>Time</span>
                <span>Status</span>
                <span>Verdict</span>
                <span>Tool</span>
                <span>Caller</span>
                <span>Detail</span>
              </div>
              {shown.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground">No entries match this filter.</div>
              ) : (
                <ul className="divide-y">
                  {shown.map(({ e, i }) => {
                    const open = openIdx === i;
                    return (
                      <li key={`${e.at}-${i}`} className={cn("relative", open && "bg-muted/30")}>
                        <span
                          className={cn("absolute inset-y-0 left-0 w-[3px]", SEV_BAR[auditSeverity(e)])}
                          aria-hidden
                        />
                        <button
                          type="button"
                          aria-expanded={open}
                          aria-controls={`audit-${i}`}
                          onClick={() => setOpenIdx(open ? null : i)}
                          className={cn(ROW_COLS, "w-full px-2 py-1.5 text-left hover:bg-muted/40")}
                        >
                          <ChevronRight
                            className={cn("h-3 w-3 text-muted-foreground transition-transform", open && "rotate-90")}
                          />
                          <span className="truncate font-mono text-[11px] tabular-nums text-muted-foreground">
                            {fmtTs(e.at)}
                          </span>
                          <span className="truncate">
                            <StatusPill status={e.status} verdict={e.verdict} />
                          </span>
                          {e.verdict ? <ActionBadge action={e.verdict as PolicyAction} /> : <span />}
                          <span className="truncate font-mono text-xs text-foreground">{e.tool}</span>
                          <span className="truncate text-xs text-muted-foreground">{principalName(e.principal)}</span>
                          <span className="flex items-center justify-between gap-2 overflow-hidden text-xs text-muted-foreground">
                            <span className={cn("truncate", e.error && "text-destructive")}>
                              {e.error
                                ? e.error
                                : e.ticket_id
                                  ? `ticket ${e.ticket_id}`
                                  : e.rule_id
                                    ? `rule ${e.rule_id}`
                                    : ""}
                            </span>
                            {e.latency_ms > 0 && <span className="shrink-0 tabular-nums">{e.latency_ms}ms</span>}
                          </span>
                        </button>
                        {open && (
                          <div id={`audit-${i}`} className="px-3 pb-3 pt-1">
                            <AuditDetail entry={e} principalName={principalName} canViewPayloads={canViewPayloads} />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// AuditDetail is the expanded inline detail for one audit row: the structured
// metadata, the full error, and (admins only) the captured request body.
function AuditDetail({
  entry,
  principalName,
  canViewPayloads,
}: {
  entry: AuditEntry;
  principalName: (id?: string) => string;
  canViewPayloads: boolean;
}) {
  const body = entry.request ? prettyJSON(entry.request) : "";
  const rows: { k: string; v: string }[] = [
    { k: "Tool", v: entry.tool },
    { k: "Caller", v: principalName(entry.principal) },
    { k: "When", v: new Date(entry.at).toLocaleString() },
    { k: "Status", v: entry.status },
    ...(entry.verdict ? [{ k: "Verdict", v: entry.verdict }] : []),
    ...(entry.ticket_id ? [{ k: "Approval ticket", v: entry.ticket_id }] : []),
    ...(entry.approver ? [{ k: "Resolved by", v: entry.approver_label || entry.approver }] : []),
    ...(entry.rule_id ? [{ k: "Rule", v: entry.rule_id }] : []),
    ...(entry.latency_ms > 0 ? [{ k: "Latency", v: `${entry.latency_ms}ms` }] : []),
  ];
  return (
    <div className="min-w-0 space-y-3">
      <dl className="space-y-1.5 rounded-md border bg-muted/20 p-3 text-sm">
        {rows.map((r) => (
          <div key={r.k} className="flex min-w-0 justify-between gap-4">
            <dt className="shrink-0 text-muted-foreground">{r.k}</dt>
            <dd className="min-w-0 truncate text-right font-mono text-xs">{r.v}</dd>
          </div>
        ))}
      </dl>
      {entry.error && (
        <div className="min-w-0 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Error</span>
            <CopyButton value={entry.error} />
          </div>
          <pre className="max-h-40 min-w-0 max-w-full overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs leading-relaxed text-destructive">
            {entry.error}
          </pre>
        </div>
      )}
      {canViewPayloads ? (
        body ? (
          <div className="min-w-0 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Request</span>
              <CopyButton value={body} />
            </div>
            <pre className="max-h-[50vh] min-w-0 max-w-full overflow-auto rounded-md border bg-muted/30 p-3 text-xs leading-relaxed">
              {body}
            </pre>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No payload captured for this call.</p>
        )
      ) : (
        <p className="text-xs text-muted-foreground">Request payloads are visible to admins only.</p>
      )}
    </div>
  );
}

// StatusPill shows the actual outcome; in audit mode a blocked-by-verdict
// call still shows status "ok", so we flag the would-block explicitly.
function StatusPill({ status, verdict }: { status: string; verdict?: string }) {
  // Resolution rows (written when a human decides a HOLD) read as their own
  // outcome, regardless of verdict.
  if (status === "approved") return <span className="shrink-0 text-xs text-success">approved</span>;
  if (status === "rejected") return <span className="shrink-0 text-xs text-destructive">rejected</span>;
  const wouldBlock = status === "ok" && (verdict === "DENY" || verdict === "HOLD");
  if (wouldBlock) {
    return <span className="shrink-0 text-xs text-warning">ran (audit)</span>;
  }
  const cls =
    status === "denied" || status === "unknown_principal" || status === "error"
      ? "text-destructive"
      : "text-muted-foreground";
  return <span className={cn("shrink-0 text-xs", cls)}>{status}</span>;
}

function ModeControl({
  mode,
  saving,
  onSet,
}: {
  mode: PolicyMode;
  saving: boolean;
  onSet: (m: PolicyMode) => void;
}) {
  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium">Enforcement</div>
          <div className="text-xs text-muted-foreground">
            {mode === "enforce"
              ? "Rules are blocking. Denied calls are rejected."
              : "Logging only — nothing is blocked. Watch the audit log to see what enforce would do."}
          </div>
        </div>
        <div className="inline-flex shrink-0 overflow-hidden rounded-md border">
          {(["audit", "enforce"] as const).map((m) => (
            <button
              key={m}
              disabled={saving}
              onClick={() => onSet(m)}
              className={cn(
                "px-3 py-1.5 text-xs font-medium capitalize transition-colors disabled:opacity-50",
                mode === m
                  ? m === "enforce"
                    ? "bg-warning/20 text-warning"
                    : "bg-primary/10 text-primary"
                  : "bg-background text-muted-foreground hover:bg-muted/50",
              )}
            >
              {m}
            </button>
          ))}
        </div>
      </div>
      {mode === "enforce" && (
        <div className="mt-2 flex items-center gap-2 text-xs text-warning">
          <ShieldAlert className="h-3.5 w-3.5 shrink-0" /> Calls not permitted by a rule (or the default below) are
          blocked.
        </div>
      )}
    </div>
  );
}

// Posture options. "ALLOW" is the default (an unset "" behaves identically —
// a normal MCP endpoint where everything is permitted).
const DEFAULT_OPTS: { value: Exclude<PolicyDefaultAction, "">; label: string; desc: string }[] = [
  {
    value: "ALLOW",
    label: "Allow all",
    desc: "Every call is allowed (a normal MCP endpoint). Add DENY or HOLD rules to restrict specific tools.",
  },
  {
    value: "DENY",
    label: "Deny all",
    desc: "Every call is blocked unless an ALLOW rule permits it — an allow-list (least privilege).",
  },
  {
    value: "HOLD",
    label: "Hold all",
    desc: "Every call needs approval unless an ALLOW rule permits it. Wire up a channel (or watch the dashboard) so holds get seen.",
  },
];

// DefaultActionControl picks the fallthrough verdict for calls no rule
// matched — the project's posture. It only bites under enforce mode (in audit
// it's logged), which the helper text makes explicit.
function DefaultActionControl({
  value,
  saving,
  onSet,
}: {
  value: PolicyDefaultAction;
  saving: boolean;
  onSet: (v: PolicyDefaultAction) => void;
}) {
  // Unset "" == allow-all for display + highlight.
  const sel: Exclude<PolicyDefaultAction, ""> = value === "" ? "ALLOW" : value;
  const active = DEFAULT_OPTS.find((o) => o.value === sel) ?? DEFAULT_OPTS[0];
  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium">Default for unmatched calls</div>
          <div className="text-xs text-muted-foreground">{active.desc}</div>
        </div>
        <div className="inline-flex shrink-0 overflow-hidden rounded-md border">
          {DEFAULT_OPTS.map((o) => (
            <button
              key={o.value}
              disabled={saving}
              onClick={() => onSet(o.value)}
              className={cn(
                "px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50",
                sel === o.value
                  ? o.value === "DENY"
                    ? "bg-destructive/15 text-destructive"
                    : o.value === "HOLD"
                      ? "bg-warning/20 text-warning"
                      : "bg-success/15 text-success"
                  : "bg-background text-muted-foreground hover:bg-muted/50",
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      {sel === "DENY" && (
        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldAlert className="h-3.5 w-3.5 shrink-0" /> Allow-list: add an ALLOW rule for each tool this agent
          needs. Pair with <span className="font-medium">enforce</span> to actually block.
        </div>
      )}
      {sel === "HOLD" && (
        <div className="mt-2 flex items-center gap-2 text-xs text-warning">
          <ShieldAlert className="h-3.5 w-3.5 shrink-0" /> Every unmatched call waits for a human. Add ALLOW rules for
          routine tools, and configure a channel so approvals get seen.
        </div>
      )}
    </div>
  );
}

